import {Avatar, Image} from 'antd'
import classNames from 'classnames'
import dayjs from 'dayjs'
import getAvatar from 'helpers/getAvatar'
import getLinkToDetail, {getAbsoluteLinkToDetail} from 'helpers/getLinkToDetail'
import getTitle from 'helpers/getTitle'
import {hasExpired} from 'helpers/datetimeHelpers'
import {copyLink} from 'helpers'
import {truncateMiddle} from 'helpers/stringUtils'
import {ImagePlaceholder} from 'conts/imageBase64'
import _ from 'lodash'
import {useAppConfig} from 'modules/local/AppConfigContext'
import useTranslate from 'modules/local/useTranslate'
import React, {useContext, useState} from 'react'
import {BiSolidChurch} from 'react-icons/bi'
import {BsClock, BsInfoCircle} from 'react-icons/bs'
import {GiIronCross} from 'react-icons/gi'
import {HiViewfinderCircle} from 'react-icons/hi2'
import {IoIosCopy} from 'react-icons/io'
import {MdAccessTime, MdEventBusy, MdOutlineEditNote, MdSchool} from 'react-icons/md'
import {PiMedalFill, PiPencilSimpleLineFill} from 'react-icons/pi'
import {Link} from 'react-router-dom'
import {LayoutContext} from 'components/layouts/Default/LayoutContext'
import {CustomEditorRenderer} from 'views/AppLocals/custom/CustomRichEditor'
import {QRComponent} from 'views/AppLocals/custom/QRComponent'
import {HeaderWithIconLine} from 'views/AppLocals/Templates/items/ListHeader'
import {NullAsDefault, renderIf, renderIfElse} from 'views/Shared'
import {isRawContentEmpty} from 'views/SlateEditor/functions/parseRawContent'
import CourseContext from '../../CourseContext'
import {WalkInEnrollmentModal} from 'views/Workspace/Course/components/WalkInEnrollmentModal'

const OwnerCard = ({item}) => {
    const t = useTranslate()
    const {org_type} = item ?? {}
    const {org_types = {}} = useAppConfig('org_types')
    const type = _.get(org_types, org_type)

    return (
        <div className="flex items-center gap-3">
            <Avatar
                size={44}
                src={getAvatar(item)}
                style={{borderRadius: 10, flexShrink: 0}}
                className="flex flex-center background-200 border border-color-50"
                icon={<BiSolidChurch size={20} className="text-color-500"/>}
            />
            <div className="flex flex-col flex-1 min-w-0">
                <Link
                    to={getLinkToDetail(item)}
                    className="font-semibold text-sm text-color-000 truncate no-underline leading-snug">
                    {getTitle(item)}
                </Link>
                {renderIf(
                    type,
                    <span className="text-xs text-blue-500 italic leading-snug">
                        {t(type)}
                    </span>
                )}
            </div>
        </div>
    )
}

/* Status-only — no button; the sticky CourseSubscriptionBar handles the action */
const EnrollmentStatus = () => {
    const t = useTranslate()
    const {item} = useContext(CourseContext)
    const {is_expired, last_enrollment_time} = item ?? {}

    const isEnded = Boolean(is_expired || hasExpired(last_enrollment_time))

    let icon, label, labelClass

    if (last_enrollment_time === null) {
        icon = <MdSchool size={15} className="text-primary flex-shrink-0"/>
        label = t('register for the course')
        labelClass = 'text-primary'
    } else if (is_expired) {
        icon = <MdEventBusy size={15} className="text-color-400 flex-shrink-0"/>
        label = t('course is over')
        labelClass = 'text-color-400'
    } else if (dayjs(last_enrollment_time).isBefore(dayjs())) {
        icon = <MdEventBusy size={15} className="text-red-500 flex-shrink-0"/>
        label = t('registration has ended')
        labelClass = 'text-red-500'
    } else {
        icon = <MdAccessTime size={15} className="text-green-500 flex-shrink-0"/>
        label = t('register for the course')
        labelClass = 'text-green-600'
    }

    return (
        <div className="flex items-center gap-2">
            {icon}
            <span className={classNames('text-sm font-medium leading-snug', labelClass)}>
                {label}
            </span>
        </div>
    )
}

const CourseDateTimeline = ({time_open, time_close, last_enrollment_time}) => {
    const t = useTranslate()
    const now = dayjs()
    const start = time_open ? dayjs(time_open) : null
    const end = time_close ? dayjs(time_close) : null
    const reg = last_enrollment_time ? dayjs(last_enrollment_time) : null

    if (!start && !end) return null

    let percent, status
    if (!start || now.isBefore(start)) {
        percent = 0
        status = 'upcoming'
    } else if (!end || now.isAfter(end)) {
        percent = 100
        status = 'ended'
    } else {
        percent = Math.round(now.diff(start) / end.diff(start) * 100)
        status = 'active'
    }

    const STATUS_CFG = {
        upcoming: {label: t('not started yet'), badgeCls: 'text-color-400 bg-color-50 border-color-100', barCls: 'bg-color-200'},
        active:   {label: t('in progress'),     badgeCls: 'text-primary bg-primary-50 border-primary-100', barCls: 'bg-primary'},
        ended:    {label: t('ended'),            badgeCls: 'text-green-600 bg-green-50 border-green-200',   barCls: 'bg-green-500'},
    }
    const {label, badgeCls, barCls} = STATUS_CFG[status]

    const regEnded = reg && now.isAfter(reg)

    return (
        <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5">
                    <BsClock size={12} className="text-color-400 flex-shrink-0"/>
                    <span className="text-xs text-color-500 italic">{`${t('time')} :`}</span>
                </div>
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${badgeCls}`}>
                    {label}
                </span>
            </div>

            <div className={`w-full h-2 rounded-full overflow-hidden bg-color-50`}>
                <div className={`h-full rounded-full ${barCls}`} style={{width: `${percent}%`}}/>
            </div>

            <div className="flex items-center justify-between text-xs text-color-400">
                <span>{start ? start.format('DD/MM/YYYY') : '—'}</span>
                <span className="font-semibold text-color-300">{percent}%</span>
                <span>{end ? end.format('DD/MM/YYYY') : '—'}</span>
            </div>

            {renderIf(
                reg,
                <div className="flex items-center gap-1.5 mt-0.5">
                    <BsClock size={10} className="text-orange-400 flex-shrink-0"/>
                    <span className="text-xs text-color-400 italic">{`${t('last enrollment time')} :`}</span>
                    <span className={`text-xs font-medium ${regEnded ? 'text-red-400 line-through' : 'text-orange-500'}`}>
                        {reg?.format('DD/MM/YYYY')}
                    </span>
                    {renderIf(
                        regEnded,
                        <span className="text-xs text-red-400 italic">({t('registration has ended')})</span>
                    )}
                </div>
            )}
        </div>
    )
}

const AboutPlaceholder = () => {
    const t = useTranslate()
    return (
        <div className="flex flex-col items-center gap-2 py-8 border border-dashed border-color-100 rounded-xl background-50">
            <MdOutlineEditNote size={32} className="text-color-300"/>
            <span className="text-sm text-color-400 italic text-center">
                {t('about course')}
            </span>
            <span className="text-xs text-color-500 italic text-center px-6">
                {t('no content')}
            </span>
        </div>
    )
}

const AdminWalkInFrame = ({courseId, subjects}) => {
    const t = useTranslate()
    const [open, setOpen] = useState(false)
    return (
        <div className="flex flex-col gap-3 p-4 rounded-2xl border border-indigo-100 bg-indigo-50">
            <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-xl bg-indigo-100 flex items-center justify-center flex-shrink-0">
                    <PiPencilSimpleLineFill size={18} className="text-indigo-500"/>
                </div>
                <div className="flex flex-col gap-0.5 flex-1">
                    <span className="text-sm font-bold text-indigo-700 tracking-wide">
                        {t('admin registration')}
                    </span>
                    <span className="text-xs text-indigo-400 italic leading-snug">
                        {t('admin can register students on behalf — walk in enrollment for new or existing students')}
                    </span>
                </div>
            </div>
            <button
                onClick={() => setOpen(true)}
                className="w-full h-10 rounded-xl font-semibold text-sm text-white bg-indigo-500 hover:bg-indigo-600 transition-colors">
                {t('walk in enrollment')}
            </button>
            <WalkInEnrollmentModal
                open={open}
                courseId={courseId}
                subjects={subjects}
                onClose={() => setOpen(false)}
                onCreated={() => setOpen(false)}
            />
        </div>
    )
}

const CourseInfoTab = () => {
    const t = useTranslate()
    const {isSm} = useContext(LayoutContext)
    const {item, renderWarnings = NullAsDefault} = useContext(CourseContext)
    const {medal, about, description, time_open, time_close, last_enrollment_time, academic_field, academic_year, edit, subjects} = item ?? {}

    const warnings = Array.from(item?.warnings ?? [])
    const is_visitor = warnings.length

    if (is_visitor) {
        return (
            <div className="w-full flex flex-col gap-3">
                {renderWarnings(warnings)}
            </div>
        )
    }

    const hasDates = _.some([time_open, time_close, last_enrollment_time])

    return renderIf(
        item,
        <div className="px-0 lg:px-2 w-full flex flex-col space-y-6">

            {/* ── Title block ── */}
            <div className="flex flex-col gap-2 mt-3">
                <div className="flex items-center gap-2">
                    <GiIronCross size={16} className="text-primary flex-shrink-0"/>
                    <h1 className="font-bold text-lg text-primary uppercase m-0 leading-tight">
                        {getTitle(item)}
                    </h1>
                </div>
                {renderIf(
                    description,
                    <p className="text-xs text-color-500 italic ml-6 m-0 leading-snug">
                        {description}
                    </p>
                )}
                {renderIf(
                    _.some([academic_field, academic_year]),
                    <div className="ml-6">
                        <span
                            style={{padding: '2px 10px', borderRadius: 999, fontSize: '0.7rem'}}
                            className="inline-flex border border-primary-300 font-medium text-primary-600 italic leading-tight">
                            {renderIfElse(
                                academic_field && academic_year,
                                `${academic_field} - ${academic_year}`,
                                academic_field || academic_year
                            )}
                        </span>
                    </div>
                )}
            </div>

            {/* ── Meta card ── */}
            <div className="background-50 rounded-xl border border-color-50 p-3 md:p-4 flex flex-col gap-3">

                <span className="text-xs text-color-500 italic">
                    {`${t('organization by')} :`}
                </span>
                <OwnerCard item={item?.owner}/>

                {hasDates && <div className="border-t border-color-50"/>}

                {renderIf(
                    hasDates,
                    <CourseDateTimeline
                        time_open={time_open}
                        time_close={time_close}
                        last_enrollment_time={last_enrollment_time}
                    />
                )}

                {renderIf(
                    medal,
                    <React.Fragment>
                        <div className="border-t border-color-50"/>
                        {/* Certificate — course output/achievement */}
                        <div className="flex flex-col gap-2">
                            <div className="flex items-center gap-2">
                                <PiMedalFill size={14} style={{color: '#ca8a04', flexShrink: 0}}/>
                                <span
                                    className="text-xs font-bold uppercase tracking-wide"
                                    style={{color: '#92400e'}}>
                                    {t('course certificate')}
                                </span>
                            </div>
                            {renderIf(
                                medal?.title,
                                <span className="text-xs text-color-400 italic -mt-1 leading-snug">
                                    {medal?.title}
                                </span>
                            )}
                            {
                                !!medal?.avatar && (
                                    <div
                                        className="relative rounded-xl overflow-hidden cursor-zoom-in"
                                        style={{
                                            background: 'var(--background-100)',
                                            border: '1.5px solid rgba(202,138,4,0.25)',
                                            boxShadow: '0 2px 12px rgba(202,138,4,0.10)',
                                        }}>
                                        <Image
                                            src={medal?.avatar || ImagePlaceholder}
                                            fallback={ImagePlaceholder}
                                            className="w-full object-contain"
                                            style={{maxHeight: 240, minHeight: 80, display: 'block'}}
                                            wrapperClassName="w-full"
                                            preview={{
                                                mask: (
                                                    <div className="flex flex-col items-center gap-1">
                                                        <HiViewfinderCircle size={22} className="text-white"/>
                                                        <span className="text-xs text-white font-medium opacity-90">
                                                    {t('tap to view')}
                                                </span>
                                                    </div>
                                                ),
                                            }}
                                        />
                                    </div>
                                )
                            }

                        </div>
                    </React.Fragment>
                )}

                {/* Enrollment status — informational only; button is in sticky bar */}
                <div className="border-t border-color-50"/>
                <EnrollmentStatus/>
            </div>

            {/* ── About course — always visible ── */}
            <div className="flex flex-col gap-2">
                <HeaderWithIconLine
                    icon={<BsInfoCircle size={18} className="text-color-500"/>}
                    title="about course"
                    className="text-lg text-color-400"
                />
                {isRawContentEmpty(about)
                    ? <AboutPlaceholder/>
                    : (
                        <CustomEditorRenderer
                            rawValue={about}
                            className="border-transparent"
                        />
                    )
                }
            </div>

            {/* ── Share / QR ── */}
            <div className="flex flex-col gap-3 p-4 border border-color-50 background-50 rounded-xl">
                <div className="flex items-center gap-2">
                    <IoIosCopy size={13} className="text-color-400 flex-shrink-0"/>
                    <span className="text-xs font-bold text-color-400 uppercase tracking-wide">
                        {t('share course')}
                    </span>
                </div>
                <div
                    onClick={() => copyLink(getAbsoluteLinkToDetail(item))}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg border border-color-50 background hover:border-primary-200 cursor-pointer transition-colors">
                    <span className="flex-1 text-xs text-blue-500 font-medium italic truncate">
                        {truncateMiddle(getAbsoluteLinkToDetail(item), isSm ? 36 : 56)}
                    </span>
                    <IoIosCopy size={13} className="text-color-300 flex-shrink-0"/>
                </div>
                <div className="flex flex-col items-center gap-2">
                    <QRComponent encode={getAbsoluteLinkToDetail(item)} defaultMaxSize={140}/>
                    <span className="text-xs text-color-400 italic text-center">
                        {t('access page by scanning the QR code')}
                    </span>
                </div>
            </div>

            {/* ── Admin walk-in frame — chỉ hiện với admin ── */}
            {renderIf(
                edit,
                <AdminWalkInFrame
                    courseId={item?.id}
                    subjects={subjects ?? []}
                />
            )}

            <div className="h-16"/>
        </div>
    )
}

export default CourseInfoTab
